import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import { useActions, useSelector } from "app/utils/hooks";
import { fetchSpaces, clearSpace } from "app/actions/space";
import {
  fetchSegmentById,
  deleteSegment,
  clearSegments,
} from "app/actions/segments";
import { RootState } from "typedefs";
import DropdownButton from "app/components/DropdownButton";
import { cls } from "app/utils";
import SpacesList from "app/routes/Segments/SegmentSpaces/SpacesList";
import SegmentsLayout from "app/components/Layouts/SegmentsLayout";
import { Link, useHistory } from "react-router-dom";
import { shallowEqual } from "react-redux";
import customPrompt from "app/components/inputs/SelectBox/helpers/customPrompt";

const SegmentSpaces = () => {
  const { t } = useTranslation();
  const id = window.location.pathname.split("/")[2];
  const spaceQuery = {
    segmentIds: [id],
    type: "athlete",
  };

  const segmentActions = useActions({
    fetchSpaces,
    clearSpace,
    fetchSegmentById,
    clearSegments,
    deleteSegment,
  });
  const history = useHistory();
  useEffect(() => {
    if (id) {
      segmentActions.fetchSpaces(spaceQuery);
      segmentActions.fetchSegmentById(id);
    }

    return () => {
      segmentActions.clearSpace();
      segmentActions.clearSegments();
    };
  }, [id]);

  const segmentData = useSelector(
    (state: RootState) => state.segments.segmentsData,
    shallowEqual,
  );

  const segmentsPending = useSelector(
    (state: RootState) => state.segments.segmentsPending,
    shallowEqual,
  );

  const canPost = segmentData && segmentData.planPermissions.includes("post");
  const canInvite =
    segmentData?.planPermissions?.includes("invite") &&
    segmentData?.spaceIds?.length > 0;
  const canManageSpaces =
    segmentData && segmentData.planPermissions.includes("manage_spaces");
  const canEdit = segmentData && segmentData.planPermissions.includes("edit");
  const canRemoveList =
    segmentData && segmentData.planPermissions.includes("delete");

  const listRemoveHandler = () => {
    customPrompt({
      message: t("Are you sure you want to remove this space from list?"),
      onYes: () => {
        segmentActions.deleteSegment(id).then(() => {
          history.push("/lists");
        });
      },
      onCancel: () => {
        return;
      },
    });
  };

  const dropdownList = [
    {
      label: t("Manage Spaces"),
      link: "spaces/edit",
      permission: canManageSpaces,
    },
    { label: t("Post into List"), link: "spaces/post", permission: canPost },
    {
      label: t("Invite Coaching Staff to all Spaces"),
      link: "add_members",
      permission: canInvite,
    },
    { label: t("Edit Name"), link: "edit", permission: canEdit },
    {
      label: t("Remove"),
      link: "",
      permission: canRemoveList,
      onClickFunc: listRemoveHandler,
    },
  ];

  const dropdownIcon = (
    <span className={styles.dropdown_icons}>
      {" "}
      <i className="icon-cog" /> <i className="ico ico-chevron" />{" "}
    </span>
  );

  const dropdownContent = (
    <DropdownButton
      btnClassName={styles.dropdown_btn}
      dropdownWrapperClass={styles.dropdown_container}
      btnContent={dropdownIcon}
    >
      <ul>
        {dropdownList.map((item, index) => {
          if (!item.permission) {
            return null;
          }

          const isRemove = item.label.includes(t("Remove"));
          const linkClassName = cls({ [styles.remove_btn]: isRemove });

          return (
            <li key={index}>
              {item.onClickFunc ? (
                <a
                  className={linkClassName}
                  onClick={(e) => {
                    e.preventDefault();
                    item.onClickFunc();
                  }}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  to={`/lists/${id}/${item.link}`}
                  className={linkClassName}
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </DropdownButton>
  );

  return (
    <SegmentsLayout isLoading={segmentsPending}>
      <div className={styles.container}>
        {segmentData && (
          <PagePanel
            title={segmentData && segmentData.name}
            headerChildren={dropdownContent}
          />
        )}
        <SpacesList />
      </div>
    </SegmentsLayout>
  );
};

export default SegmentSpaces;
